import { signIn, signOut, getCurrentUser, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { User } from '../types/store';
import { UserUtil, UserProfile } from '../utils/UserUtil';
import UserService from './UserService';

export interface AuthError {
  code: string;
  message: string;
}

/**
 * Consolidated Authentication Service
 * Replaces GoogleOAuthService, GoogleSignInService, and IdentityProviderService
 * Uses standard Amplify patterns with custom challenge support for account linking
 */
export class AuthService {
  private static instance: AuthService;
  private currentUserPromise: Promise<User | null> | null = null;
  
  constructor() {
    // No longer need GoogleAccountLinkingService - using direct Amplify methods
  }
  
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Sign in with Google using Amplify's built-in federated sign-in
   * Account linking is now handled automatically by the Pre-signup Lambda
   */
  async signInWithGoogle(): Promise<void> {
    try {
      console.log('🔍 [AuthService] Starting Google sign-in - account linking handled by Lambda');
      
      // Use Amplify's hosted UI for Google sign-in
      // The Pre-signup Lambda will automatically handle account linking
      await signInWithRedirect({ provider: 'Google' });
      
      console.log('✅ [AuthService] Google sign-in initiated - Lambda handles linking');
      
    } catch (error: any) {
      console.error('❌ [AuthService] Google sign-in error:', error);
      throw error;
    }
  }



  /**
   * Sign out user
   */
  async signOut(): Promise<void> {
    try {
      console.log('🔍 [AuthService] signOut() called - Stack trace:');
      console.trace();
      await signOut();
      console.log('✅ [AuthService] Sign out successful');
    } catch (error) {
      console.error('❌ [AuthService] Sign out error:', error);
      throw error;
    }
  }

  /**
   * Get current authenticated user with effective user ID resolution
   * Uses request deduplication to prevent multiple concurrent calls
   */
  async getCurrentUser(): Promise<User | null> {
    // If there's already a request in progress, return that promise
    if (this.currentUserPromise) {
      return this.currentUserPromise;
    }

    // Create a new promise and cache it
    this.currentUserPromise = this._getCurrentUserInternal();
    
    try {
      const result = await this.currentUserPromise;
      return result;
    } finally {
      // Clear the cached promise after completion
      this.currentUserPromise = null;
    }
  }

  /**
   * Internal method to get current user (actual implementation)
   */
  private async _getCurrentUserInternal(): Promise<User | null> {
    try {
      const cognitoUser = await getCurrentUser();
      const session = await fetchAuthSession();
      
      // Get user attributes from session
      const attributes = session.tokens?.idToken?.payload || {};
      
      // Try to fetch user profile to get appUserId
      let effectiveUserId = cognitoUser.username;
      let userProfile: UserProfile | null = null;
      
      try {
        const userService = new UserService();
        const fetchedProfile = await userService.getUserProfile(cognitoUser.username);
        
        // Convert UserService.UserProfile to UserUtil.UserProfile
        // Handle the userName vs username field mapping from backend
        userProfile = {
          userName: cognitoUser.username,
          appUserId: fetchedProfile.appUserId, // This comes from the backend response
          handle: fetchedProfile.handle,
          // Add other fields as needed
        };
        
        // Use the centralized utility to get effective user ID
        effectiveUserId = UserUtil.getEffectiveUserId(userProfile);
        
        console.log(`🔍 [AuthService] Cognito user: ${cognitoUser.username}, Effective user: ${effectiveUserId}`);
        console.log(`🔍 [AuthService] User profile appUserId: ${fetchedProfile.appUserId}`);
        console.log(`🔍 [AuthService] UserProfile object:`, userProfile);
      } catch (profileError) {
        console.log('🔍 [AuthService] Could not fetch user profile, using Cognito username:', profileError);
        // Fall back to Cognito username if profile fetch fails
      }
      
      return this.transformCognitoUser(cognitoUser, attributes, effectiveUserId);
    } catch (error) {
      console.log('🔍 [AuthService] No authenticated user found');
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current session (for API calls)
   */
  async getCurrentSession() {
    try {
      return await fetchAuthSession();
    } catch (error) {
      console.error('❌ [AuthService] Failed to get current session:', error);
      throw error;
    }
  }

  /**
   * Transform Cognito user to our User type
   */
  private transformCognitoUser(cognitoUser: any, attributes: any = {}, effectiveUserId?: string): User {
    return {
      id: effectiveUserId || cognitoUser.username,
      username: effectiveUserId || cognitoUser.username,
      email: attributes?.email || '',
      profilePhotoId: attributes?.picture,
      attributes: attributes
    };
  }

  /**
   * Set up authentication event listeners with debouncing and circuit breaker
   */
  setupAuthListener(callback: (event: string, data?: any) => void): () => void {
    let eventCount = 0;
    let lastEventTime = 0;
    let rapidEventCount = 0;
    const DEBOUNCE_MS = 1000; // 1 second debounce
    const CIRCUIT_BREAKER_THRESHOLD = 10; // Max events in rapid succession
    const CIRCUIT_BREAKER_RESET_MS = 5000; // Reset circuit breaker after 5 seconds
    
    // Events to ignore to prevent infinite loops
    const IGNORED_EVENTS = ['tokenRefresh', 'tokenRefresh_failure'];
    
    const authHandler = (data: any) => {
      const now = Date.now();
      const event = data.payload.event;
      const eventData = data.payload.data;
      
      eventCount++;
      
      // Check if this is an ignored event
      if (IGNORED_EVENTS.includes(event)) {
        console.log(`🔍 [AuthService] Ignoring event #${eventCount}: ${event} (filtered to prevent loops)`);
        return;
      }
      
      // Circuit breaker: detect rapid-fire events
      if (now - lastEventTime < DEBOUNCE_MS) {
        rapidEventCount++;
        if (rapidEventCount > CIRCUIT_BREAKER_THRESHOLD) {
          console.warn(`🚨 [AuthService] Circuit breaker triggered! Too many rapid events (${rapidEventCount}). Ignoring event: ${event}`);
          return;
        }
      } else {
        // Reset rapid event counter if enough time has passed
        rapidEventCount = 0;
      }
      
      lastEventTime = now;
      
      console.log(`🔍 [AuthService] Processing auth event #${eventCount}:`, event);
      console.log('🔍 [AuthService] Full payload:', JSON.stringify(data.payload, null, 2));
      
      if (event === 'signedOut') {
        console.log('🚨 [AuthService] SIGNEDOUT EVENT DETECTED - Stack trace:');
        console.trace();
      }
      
      // Handle auth events - account linking is now handled by Lambda
      callback(event, eventData);
    };

    console.log('🔍 [AuthService] Setting up auth listener with debouncing and circuit breaker');
    const unsubscribe = Hub.listen('auth', authHandler);
    
    // Reset circuit breaker periodically
    const circuitBreakerReset = setInterval(() => {
      if (rapidEventCount > 0) {
        console.log(`🔍 [AuthService] Resetting circuit breaker (was at ${rapidEventCount} rapid events)`);
        rapidEventCount = 0;
      }
    }, CIRCUIT_BREAKER_RESET_MS);
    
    // Return cleanup function
    return () => {
      console.log('🔍 [AuthService] Cleaning up auth listener and circuit breaker');
      clearInterval(circuitBreakerReset);
      unsubscribe();
    };
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();
